import type { AppState } from '../types';
import { initialState } from './domain';

const collections = ['estates', 'fields', 'plans', 'activities', 'tasks', 'attachments'] as const;
const isDemo = (id: string) => typeof id === 'string' && id.startsWith('example-');

// These values are presentation/server metadata, not customer edits.
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !['updatedAt', 'storage', 'capturedAt', 'checkedAt'].includes(key))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => [key, canonical(item)]),
    );
  return value;
}
export function customerContent(state: AppState): unknown {
  return canonical({
    ...Object.fromEntries(
      collections.map((key) => [key, [...state[key]].sort((a, b) => a.id.localeCompare(b.id))]),
    ),
    profile: { name: state.profile.name, company: state.profile.company },
  });
}
export function sameCustomerContent(a: AppState, b: AppState): boolean {
  return JSON.stringify(customerContent(a)) === JSON.stringify(customerContent(b));
}
const equal = (a: unknown, b: unknown) =>
  JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));

export function reconcileState(
  base: AppState | null,
  local: AppState,
  remote: AppState,
): AppState | null {
  const activityHistory = [
    ...new Map(
      [...(remote.activityHistory ?? []), ...(local.activityHistory ?? [])].map((event) => [
        event.id,
        event,
      ]),
    ).values(),
  ];

  if (sameCustomerContent(local, remote)) {
    return {
      ...remote,
      activityHistory,
      selectedEstateId: local.selectedEstateId || remote.selectedEstateId,
      selectedPlanId: local.selectedPlanId || remote.selectedPlanId,
    };
  }

  const initial = initialState();
  const isDemoEntity = (key: (typeof collections)[number], item: { id: string }) => {
    const defaultItem = (initial[key] as Array<{ id: string }>).find((x) => x.id === item.id);
    return defaultItem !== undefined && equal(item, defaultItem);
  };

  const nameMatchesOrEmpty =
    !local.profile.name || !remote.profile.name || local.profile.name === remote.profile.name;
  const companyMatchesOrEmpty =
    !local.profile.company ||
    !remote.profile.company ||
    local.profile.company === remote.profile.company;

  if (!base) {
    // 1. Fresh empty server account: remote has no estates, fields or plans, but local has custom user/guest data
    const remoteIsEmpty =
      remote.estates.length === 0 && remote.fields.length === 0 && remote.plans.length === 0;
    if (
      remoteIsEmpty &&
      local.estates.length > 0 &&
      local.fields.some((f) => !isDemo(f.id)) &&
      nameMatchesOrEmpty &&
      companyMatchesOrEmpty
    ) {
      return {
        ...local,
        profile: {
          name: local.profile.name || remote.profile.name,
          company: local.profile.company || remote.profile.company,
          email: remote.profile.email || local.profile.email,
        },
        activityHistory,
      };
    }

    // 2. If local only contains unedited demo entities and has compatible profile info, remote is authoritative
    const localHasNoCustomEntities = collections.every((key) =>
      local[key].every((item) => isDemoEntity(key, item)),
    );
    if (localHasNoCustomEntities && nameMatchesOrEmpty && companyMatchesOrEmpty) {
      return {
        ...remote,
        profile: {
          name: remote.profile.name || local.profile.name,
          company: remote.profile.company || local.profile.company,
          email: remote.profile.email || local.profile.email,
        },
        activityHistory,
        selectedEstateId: remote.selectedEstateId,
        selectedPlanId: remote.selectedPlanId,
      };
    }

    // 3. Legacy subset: all items that exist locally exist identically in remote
    const legacySubset =
      nameMatchesOrEmpty &&
      companyMatchesOrEmpty &&
      collections.every((key) =>
        local[key].every((item) => {
          const other = remote[key].find((row) => row.id === item.id);
          return other !== undefined && equal(item, other);
        }),
      );

    if (legacySubset) {
      return {
        ...remote,
        profile: {
          name: remote.profile.name || local.profile.name,
          company: remote.profile.company || local.profile.company,
          email: remote.profile.email || local.profile.email,
        },
        activityHistory,
        selectedEstateId: local.selectedEstateId || remote.selectedEstateId,
        selectedPlanId: local.selectedPlanId || remote.selectedPlanId,
      };
    }

    // 4. Local has a newly added estate (e.g. transferred guest revir) while preserving existing remote estates
    const newEstatesInLocal = local.estates.filter(
      (e) => !remote.estates.some((re) => re.id === e.id),
    );
    const existingRemoteEstatesPreserved =
      nameMatchesOrEmpty &&
      companyMatchesOrEmpty &&
      remote.estates.length > 0 &&
      newEstatesInLocal.length > 0 &&
      remote.estates.every((re) => {
        const localEstate = local.estates.find((e) => e.id === re.id);
        if (!localEstate || !equal(re, localEstate)) return false;
        const remoteFieldsForEstate = remote.fields.filter((f) => f.estateId === re.id);
        const localFieldsForEstate = local.fields.filter((f) => f.estateId === re.id);
        return (
          remoteFieldsForEstate.length === localFieldsForEstate.length &&
          remoteFieldsForEstate.every((rf) => {
            const lf = localFieldsForEstate.find((f) => f.id === rf.id);
            return lf !== undefined && equal(rf, lf);
          })
        );
      });

    if (existingRemoteEstatesPreserved) {
      return {
        ...remote,
        profile: {
          name: remote.profile.name || local.profile.name,
          company: remote.profile.company || local.profile.company,
          email: remote.profile.email || local.profile.email,
        },
        activityHistory,
        estates: [
          ...remote.estates,
          ...local.estates.filter((e) => !remote.estates.some((re) => re.id === e.id)),
        ],
        fields: [
          ...remote.fields,
          ...local.fields.filter((f) => !remote.fields.some((rf) => rf.id === f.id)),
        ],
        plans: [
          ...remote.plans,
          ...local.plans.filter((p) => !remote.plans.some((rp) => rp.id === p.id)),
        ],
        activities: [
          ...remote.activities,
          ...local.activities.filter((a) => !remote.activities.some((ra) => ra.id === a.id)),
        ],
        tasks: [
          ...remote.tasks,
          ...local.tasks.filter((t) => !remote.tasks.some((rt) => rt.id === t.id)),
        ],
        attachments: [
          ...remote.attachments,
          ...local.attachments.filter((at) => !remote.attachments.some((rat) => rat.id === at.id)),
        ],
        selectedEstateId: local.selectedEstateId || remote.selectedEstateId,
        selectedPlanId: local.selectedPlanId || remote.selectedPlanId,
      };
    }

    return null;
  }

  function merge(before: any, ours: any, theirs: any): any {
    if (equal(ours, before)) return theirs;
    if (equal(theirs, before) || equal(ours, theirs)) return ours;
    if (!before || !ours || !theirs || Array.isArray(before) || typeof before !== 'object')
      throw new Error('conflict');
    const result: Record<string, unknown> = {};
    for (const key of new Set([
      ...Object.keys(before),
      ...Object.keys(ours),
      ...Object.keys(theirs),
    ])) {
      result[key] = ['updatedAt', 'storage', 'capturedAt', 'checkedAt'].includes(key)
        ? (ours[key] ?? theirs[key])
        : merge(before[key], ours[key], theirs[key]);
    }
    return result;
  }
  try {
    const result: AppState = {
      ...remote,
      activityHistory,
      profile: {
        ...merge(
          { name: base.profile.name, company: base.profile.company },
          { name: local.profile.name, company: local.profile.company },
          { name: remote.profile.name, company: remote.profile.company },
        ),
        email: remote.profile.email,
      },
      selectedEstateId: local.selectedEstateId,
      selectedPlanId: local.selectedPlanId,
    };
    for (const key of collections) {
      const rows = [base[key], local[key], remote[key]].map(
        (items) => new Map(items.map((item) => [item.id, item])),
      );
      const ids = new Set(rows.flatMap((items) => [...items.keys()]));
      (result[key] as unknown[]) = [...ids]
        .map((id) => merge(rows[0].get(id), rows[1].get(id), rows[2].get(id)))
        .filter(Boolean);
    }
    if (!result.estates.some((estate) => estate.id === result.selectedEstateId))
      result.selectedEstateId = result.estates[0]?.id ?? '';
    if (
      !result.plans.some(
        (plan) => plan.id === result.selectedPlanId && plan.estateId === result.selectedEstateId,
      )
    )
      result.selectedPlanId =
        result.plans.find((plan) => plan.estateId === result.selectedEstateId)?.id ?? '';
    return result;
  } catch {
    return null;
  }
}
